import React from "react";
import { ArrowRight, Link as LinkIcon, CheckCircle2, Phone, Mail, Clock, LifeBuoy } from "lucide-react";

// --- DUMMY DATA ---
const INTRO_FEATURES = ["Manage passport details", "Manage your travel preferences", "Manage your loyalty cards", "View ongoing trips and create new ones"];

const USEFUL_LINKS = [
  { title: "How To Guides", url: "#" },
  { title: "Electronic Travel Authorisation (ETA)", url: "#" },
  { title: "FCO Travel Advice", url: "#" },
  { title: "USA Esta Application", url: "#" },
];

// --- REUSABLE COMPONENTS ---

// Standard White Card Wrapper
const Card = ({ children, className = "" }) => <div className={`mb-6 break-inside-avoid rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow hover:shadow-md ${className}`}>{children}</div>;

// Primary Action Button (Using a vibrant complementary purple to pop against #572f5f)
const ActionButton = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-2 rounded-md bg-[#8b5cf6] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#7c3aed] hover:shadow-md focus:ring-4 focus:ring-[#8b5cf6]/30">
    {children}
    <ArrowRight
      size={16}
      className="transition-transform group-hover:translate-x-1"
    />
  </button>
);

// Dark Action Button (Used on the dark #572f5f background)
const DarkButton = ({ children, onClick }) => (
  <button
    onClick={onClick}
    className="group flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-5 py-2.5 text-sm font-bold text-white backdrop-blur-sm transition-all hover:bg-white hover:text-[#572f5f]">
    {children}
    <ArrowRight
      size={16}
      className="transition-transform group-hover:translate-x-1"
    />
  </button>
);

const TravelHome = () => {
  return (
    <div className="mx-auto max-w-7xl p-4 md:p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-black uppercase tracking-tight text-[#572f5f] md:text-4xl">
          Welcome, Admin Account <span className="animate-wave inline-block">👋</span>
        </h1>
      </div>

      {/* Masonry Grid Layout */}
      <div className="columns-1 gap-6 lg:columns-2">
        {/* Intro Card 1 */}
        <Card>
          <h3 className="mb-4 text-lg font-bold leading-snug text-[#572f5f]">YourCT is easy to use and allows you to manage your travel profile details so that we can organise your trips.</h3>
          <ul className="mb-6 space-y-3">
            {INTRO_FEATURES.map((feature, idx) => (
              <li
                key={idx}
                className="flex items-start gap-3 text-sm font-medium text-gray-600">
                <CheckCircle2
                  size={18}
                  className="mt-0.5 shrink-0 text-[#8b5cf6]"
                />
                {feature}
              </li>
            ))}
          </ul>
          <p className="text-xs font-medium text-gray-500">All information is encrypted and stored securely, so your data is in safe hands. To keep this secure, please do not share your login or password with anyone.</p>
        </Card>

        {/* Action Card: Complicated Trip */}
        <Card>
          <h3 className="mb-2 text-xl font-black uppercase text-[#572f5f]">Need to book a complicated trip?</h3>
          <p className="mb-6 text-sm font-medium text-gray-600">For complex, multi-stop trips, click the link below to send a travel request through to your travel team</p>
          <ActionButton>Create New Request</ActionButton>
        </Card>

        {/* Promo Card: Stay Updated */}
        <div className="mb-6 flex break-inside-avoid overflow-hidden rounded-xl bg-[#572f5f] shadow-md">
          <div className="flex w-3/5 flex-col items-start justify-center p-6 text-white sm:p-8">
            <h3 className="mb-2 text-xl font-black uppercase text-[#e9d5ff]">Stay Updated</h3>
            <p className="mb-6 text-sm font-medium leading-relaxed text-gray-200">If your details such as passport information change at any time, it is essential that you update your travel profile before booking any travel.</p>
            <DarkButton>Update Profile</DarkButton>
          </div>
          <div className="relative w-2/5 bg-gray-200">
            {/* Placeholder Image */}
            <img
              src="https://images.unsplash.com/photo-1544644181-1484b3fdfc62?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80"
              alt="Business traveler"
              className="absolute h-full w-full object-cover opacity-90 mix-blend-overlay"
            />
            {/* Violet tint over the image matching your exact hex code */}
            <div className="absolute inset-0 bg-[#572f5f]/40"></div>
          </div>
        </div>

        {/* Contact Card */}
        <Card>
          <h3 className="mb-6 text-xl font-black uppercase text-[#572f5f]">Need help or need to change a reservation? Simply contact the team:</h3>
          <div className="space-y-4 text-sm font-medium text-gray-600">
            <div className="flex items-center gap-3">
              <Mail
                size={16}
                className="text-[#8b5cf6]"
              />
              <p>
                Email -{" "}
                <a
                  href="mailto:enquire@ctbusinesstravel.co.uk"
                  className="font-bold text-[#8b5cf6] hover:underline">
                  enquire@ctbusinesstravel.co.uk
                </a>
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Phone
                size={16}
                className="text-[#8b5cf6]"
              />
              <p>Phone - +44 (0)1892 616 122</p>
            </div>
            <div className="flex items-start gap-3 pt-2">
              <Clock
                size={16}
                className="mt-0.5 shrink-0 text-[#572f5f]"
              />
              <p>
                <span className="font-bold text-[#572f5f]">Office Hours</span> – Monday-Friday 0800 - 1800 (excluding public holidays)
              </p>
            </div>
            <div className="flex items-start gap-3">
              <LifeBuoy
                size={16}
                className="mt-0.5 shrink-0 text-[#572f5f]"
              />
              <p>
                <span className="font-bold text-[#572f5f]">Available 24/7</span> – please note that calls outside our office hours may be subject to additional transaction fees.
              </p>
            </div>
          </div>
        </Card>

        {/* Action Card: Book Online */}
        <Card>
          <h3 className="mb-2 text-xl font-black uppercase text-[#572f5f]">Book Travel Online</h3>
          <p className="mb-6 text-sm font-medium text-gray-600">Click the link below to book a new trip online.</p>
          <ActionButton>Book New Trip</ActionButton>
        </Card>

        {/* Useful Links Card */}
        <Card className="bg-gray-50/50">
          <h3 className="mb-6 text-xl font-black uppercase text-[#572f5f]">Useful Links</h3>
          <div className="space-y-3">
            {USEFUL_LINKS.map((link, idx) => (
              <a
                key={idx}
                href={link.url}
                className="group flex items-center justify-between rounded-lg border border-gray-100 bg-white p-4 font-bold text-gray-700 shadow-sm transition-all hover:border-[#572f5f]/30 hover:text-[#572f5f] hover:shadow-md">
                {link.title}
                <LinkIcon
                  size={16}
                  className="text-gray-400 transition-colors group-hover:text-[#572f5f]"
                />
              </a>
            ))}
          </div>
        </Card>
      </div>

      {/* Footer Logos */}
      <div className="mt-12 flex flex-col items-center justify-center border-t border-gray-200 pb-4 pt-8">
        <div className="mb-4 flex flex-wrap items-center justify-center gap-6 opacity-60 grayscale transition-all hover:grayscale-0">
          <div className="text-xl font-black text-gray-400">BTA</div>
          <div className="text-xl font-black text-gray-400">FOCUS</div>
          <div className="text-xl font-black text-gray-400">GlobalStar</div>
          <div className="text-xl font-black text-gray-400">ABTA</div>
          <div className="text-xl font-black text-gray-400">IATA</div>
        </div>
        <p className="text-xs font-medium text-gray-400">Version: 2026.11.12.2 © 2026 - CT Business Travel</p>
      </div>
    </div>
  );
};

export default TravelHome;
